import React, { useCallback, useContext, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import { getUsers, setUserActive } from "../../../api/userApi";
import { LanguageContext } from "../../../components/LanguageContext";
import { isAdminOrOwner, isOwner } from "../../../utils/permissions";

const typeLabel = (t, translations) => {
    const u = (t || "").toUpperCase();
    if (u === "O") return translations?.roleOwner || "Owner";
    if (u === "A") return translations?.roleAdmin || "Administrator";
    return translations?.roleUser || "Korisnik";
};

const UsersPage = () => {
    const { translations } = useContext(LanguageContext) || {};
    const queryClient = useQueryClient();
    const owner = isOwner();
    const canCreate = isAdminOrOwner();

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const { data, isLoading } = useQuery({
        queryKey: ["users"],
        queryFn: () => getUsers(),
        staleTime: 5 * 60 * 1000,
    });

    const mutationActive = useMutation({
        mutationFn: (formData) => setUserActive(formData),
        onSuccess: (resp) => {
            setError("");
            setSuccess(resp.data.message);
            queryClient.invalidateQueries(["users"]);
        },
        onError: (err) => {
            setSuccess("");
            setError(err.response?.data?.message || "");
        },
    });

    const handleToggleActive = useCallback(
        (id_user, currentlyActive) => {
            const formData = new FormData();
            formData.append("id_user", id_user);
            formData.append("active", currentlyActive ? 0 : 1);
            mutationActive.mutateAsync(formData).catch(() => {});
        },
        [mutationActive]
    );

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminUsersPageID || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "name",
                headerName: translations?.adminUsersPageName || "Ime",
                minWidth: 130,
                flex: 1,
            },
            {
                field: "surname",
                headerName: translations?.adminUsersPageSurname || "Prezime",
                minWidth: 130,
                flex: 1,
            },
            {
                field: "email",
                headerName: translations?.adminUsersPageEmail || "E-mail",
                minWidth: 220,
                flex: 1.5,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "type",
                headerName: translations?.adminUsersPageRole || "Uloga",
                minWidth: 150,
                flex: 0.8,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const t = (params.row.type || "").toUpperCase();
                    return (
                        <StatusBadge
                            status={t === "O" ? "info" : "neutral"}
                            showDot={false}
                            label={typeLabel(t, translations)}
                        />
                    );
                },
            },
            {
                field: "active",
                headerName: translations?.active || "Aktivan",
                minWidth: 180,
                flex: 0.8,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const active = Number(params.row.active) === 1;
                    const targetIsOwner =
                        (params.row.type || "").toUpperCase() === "O";
                    const canToggle = owner && !targetIsOwner;
                    return (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%" }}
                        >
                            <StatusBadge
                                status={active ? "active" : "inactive"}
                                label={
                                    active
                                        ? translations?.activeStore || "Aktivan"
                                        : translations?.disabledStore || "Neaktivan"
                                }
                            />
                            {canToggle && (
                                <Tooltip
                                    title={
                                        active
                                            ? "Deaktiviraj korisnika"
                                            : "Aktiviraj korisnika"
                                    }
                                >
                                    <Switch
                                        size="small"
                                        checked={active}
                                        onChange={() =>
                                            handleToggleActive(
                                                params.row.id,
                                                active
                                            )
                                        }
                                    />
                                </Tooltip>
                            )}
                        </Stack>
                    );
                },
            },
            {
                field: "info",
                headerName: translations?.adminUsersPageInfo || "Informacije",
                minWidth: 140,
                flex: 0.8,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Button
                        component={RouterLink}
                        to={`/korisnik/${params.row.id}`}
                        variant="text"
                        size="small"
                        endIcon={<ChevronRightRoundedIcon />}
                    >
                        {translations?.adminUsersPageInfo || "Detalji"}
                    </Button>
                ),
            },
        ],
        [translations, owner, handleToggleActive]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<PeopleAltOutlinedIcon />}
                eyebrow={translations?.adminAdministratorMenuUsers || "Users"}
                title={translations?.adminUsersPageName || "Korisnici"}
                description={
                    translations?.usersSubtitle ||
                    "Pregled svih korisnika sistema."
                }
                actions={
                    canCreate ? (
                        <Button
                            component={RouterLink}
                            to="/novi-korisnik"
                            variant="contained"
                            startIcon={<PersonAddAlt1OutlinedIcon />}
                        >
                            {translations?.adminUsersPageNewUser ||
                                "Novi korisnik"}
                        </Button>
                    ) : null
                }
            />

            <Stack spacing={2}>
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")}>
                        {success}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <SectionCard padded={false}>
                    <DataTable
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        mutating={mutationActive.isLoading}
                        height={620}
                        pageSize={10}
                        pageSizeOptions={[10, 25, 50]}
                        emptyIcon={<PeopleAltOutlinedIcon />}
                        emptyTitle={
                            translations?.noUsersTitle || "Nema korisnika"
                        }
                        emptyDescription={
                            canCreate
                                ? translations?.noUsersDescription ||
                                  "Kreirajte prvog korisnika klikom na dugme iznad."
                                : "Nema drugih korisnika u sistemu."
                        }
                    />
                </SectionCard>
            </Stack>
        </Box>
    );
};

export default UsersPage;